import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { Header } from "../users.const";
import { SERVICE_ACCOUNTS_TAB } from "../service-accounts.const";
import { Client, CityOption, PaginationParams, TabType, User } from "../users.type";
import { DataTableUser } from "./users-table/data-table-user";
import { ServiceAccountsTable } from "./service-accounts/service-accounts-table";
import { RoleType } from "@/store/auth/auth.type";
import { OnChangeFn, SortingState } from "@tanstack/react-table";

export type UserPanelProps = {
  currentPage: PaginationParams;
  setCurrentPage: (page: number) => void;
  loading: boolean;
  userType: TabType;
  setUserType: React.Dispatch<React.SetStateAction<TabType>>;
  users: User[] | Client[];
  totalPage: number;
  onRefetch?: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  cities?: CityOption[];
  cityId?: string;
  onCityChange?: (id: string) => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
};

export const UserPanel = ({
  loading,
  users,
  userType,
  currentPage,
  setCurrentPage,
  totalPage,
  setUserType,
  onRefetch,
  searchValue,
  onSearchChange,
  pageSize,
  onPageSizeChange,
  cities,
  cityId,
  onCityChange,
  sorting,
  onSortingChange,
}: UserPanelProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentRole = searchParams.get("role") ?? userType;
  const isServiceAccounts = currentRole === SERVICE_ACCOUNTS_TAB;

  const handleTabChange = (value: string) => {
    const tab = value as TabType;
    const pageSize = searchParams.get("pageSize");
    setSearchParams({ role: tab, page: "1", ...(pageSize ? { pageSize: pageSize } : {}) });
    setUserType(tab);
  };

  return (
    <>
      <Tabs
        defaultValue={userType}
        value={currentRole}
        onValueChange={handleTabChange}
        className="flex w-full flex-col justify-start"
      >
        <TabsList className="flex gap-1 p-1 rounded-md bg-muted">
          {Header.map((tab) => (
            <TabsTrigger
              key={tab}
              value={tab}
              disabled={loading && !isServiceAccounts}
              className={`rounded-md px-4 py-2 text-xs text-muted-foreground ${
                loading && !isServiceAccounts ? "cursor-not-allowed" : "cursor-pointer"
              } ${currentRole === tab ? "bg-white" : "hover:bg-gray-200"}`}
            >
              {tab}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div>
        {isServiceAccounts ? (
          <ServiceAccountsTable currentPage={currentPage} setCurrentPage={setCurrentPage} />
        ) : (
          <DataTableUser
            userType={userType as RoleType}
            data={users}
            loading={loading}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPage={totalPage}
            onRefetch={onRefetch}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            hideSearch={searchValue === undefined}
            cities={cities}
            cityId={cityId}
            onCityChange={onCityChange}
            sorting={sorting}
            onSortingChange={onSortingChange}
          />
        )}
      </div>
    </>
  );
};
